import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fmtINR } from "@/lib/api";
import { useProfiles } from "@/lib/profiles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Save, Printer, X, ArrowLeft, Download, ArrowRightLeft, ListPlus, Search } from "lucide-react";
import { toast } from "sonner";
import InvoiceDocument from "@/components/InvoiceDocument";
import html2pdf from "html2pdf.js";

const fieldCls = "bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-[#D4AF37] text-white font-mont placeholder:text-neutral-600 h-auto";
const labelCls = "font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500";
const round = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

const newItem = () => ({ id: `li_${Math.random().toString(36).slice(2, 10)}`, description: "", quantity: 1, rate: 0, amount: 0 });

export default function QuotationEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();

  const [profiles, setProfiles] = useState([]);
  const [clients, setClients] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const { activeProfileId } = useProfiles();

  const [catalogItems, setCatalogItems] = useState([]);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [newCatName, setNewCatName] = useState("");
  const [newCatPrice, setNewCatPrice] = useState("");

  const loadCatalog = async (profileId) => {
    if (!profileId) { setCatalogItems([]); return; }
    const { data } = await api.get(`/items?profile_id=${profileId}`);
    setCatalogItems(data);
  };

  const [quo, setQuo] = useState({
    quotation_number: "",
    profile_id: "",
    client_id: "",
    issue_date: new Date().toISOString().slice(0, 10),
    valid_until: "",
    items: [newItem()],
    notes: "",
    gst_rate: 18,
    gst_type: "CGST_SGST",
    discount: 0,
    discount_type: "flat",
    terms: "",
    status: "draft",
    theme_id: "classic-gold",
    converted_invoice_id: "",
  });

  useEffect(() => {
    (async () => {
      const pr = await api.get("/profiles");
      setProfiles(pr.data);
      if (isNew) {
        const defaultProfile = pr.data.find((p) => p.id === activeProfileId)?.id || pr.data[0]?.id || "";
        const [cl, num] = await Promise.all([
          defaultProfile ? api.get(`/clients?profile_id=${defaultProfile}`) : Promise.resolve({ data: [] }),
          defaultProfile ? api.get(`/quotations/next-number?profile_id=${defaultProfile}`) : Promise.resolve({ data: { quotation_number: "" } }),
        ]);
        setClients(cl.data);
        loadCatalog(defaultProfile);
        setQuo((v) => ({ ...v, quotation_number: num.data.quotation_number, profile_id: defaultProfile, client_id: cl.data[0]?.id || "" }));
      } else {
        const { data } = await api.get(`/quotations/${id}`);
        const cl = await api.get(`/clients?profile_id=${data.profile_id}`);
        setClients(cl.data);
        loadCatalog(data.profile_id);
        setQuo({
          quotation_number: data.quotation_number,
          profile_id: data.profile_id,
          client_id: data.client_id,
          issue_date: data.issue_date,
          valid_until: data.valid_until || "",
          items: data.items?.length ? data.items : [newItem()],
          notes: data.notes || "",
          gst_rate: data.gst_rate,
          gst_type: data.gst_type,
          discount: data.discount || 0,
          discount_type: data.discount_type || "flat",
          terms: data.terms || "",
          status: data.status || "draft",
          theme_id: data.theme_id || "classic-gold",
          converted_invoice_id: data.converted_invoice_id || "",
        });
      }
    })();
  }, [id, isNew, activeProfileId]);

  // Refetch clients & catalog whenever the selected business changes (e.g. user switches "From Business")
  const initialLoadDone = React.useRef(false);
  useEffect(() => {
    if (!initialLoadDone.current) { initialLoadDone.current = true; return; }
    if (!quo.profile_id) return;
    (async () => {
      const cl = await api.get(`/clients?profile_id=${quo.profile_id}`);
      setClients(cl.data);
      loadCatalog(quo.profile_id);
      if (!cl.data.find((c) => c.id === quo.client_id)) {
        setQuo((v) => ({ ...v, client_id: cl.data[0]?.id || "" }));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quo.profile_id]);

  const isConverted = quo.status === "converted";

  const totals = useMemo(() => {
    const subtotal = quo.items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.rate || 0), 0);
    const dval = Number(quo.discount || 0);
    const discount_amount = quo.discount_type === "percent" ? subtotal * (dval / 100) : dval;
    const taxable = Math.max(0, subtotal - discount_amount);
    const gst_amount = taxable * (Number(quo.gst_rate || 0) / 100);
    const grand_total = taxable + gst_amount;
    return {
      subtotal: round(subtotal), discount_amount: round(discount_amount), taxable: round(taxable),
      gst_amount: round(gst_amount), grand_total: round(grand_total), paid: 0, balance_due: 0,
    };
  }, [quo]);

  const setItem = (idx, key, val) => {
    setQuo((v) => {
      const items = [...v.items];
      items[idx] = { ...items[idx], [key]: val };
      items[idx].amount = Number(items[idx].quantity || 0) * Number(items[idx].rate || 0);
      return { ...v, items };
    });
  };
  const addItem = () => setQuo((v) => ({ ...v, items: [...v.items, newItem()] }));
  const removeItem = (idx) => setQuo((v) => ({ ...v, items: v.items.filter((_, i) => i !== idx) }));

  const addFromCatalog = (catItem) => {
    setQuo((v) => ({
      ...v,
      items: [...v.items, { id: `li_${Math.random().toString(36).slice(2, 10)}`, description: catItem.name, quantity: 1, rate: catItem.sales_price, amount: catItem.sales_price }],
    }));
    toast.success(`Added "${catItem.name}"`);
  };

  const [savingCatalogItem, setSavingCatalogItem] = useState(false);
  const quickCreateCatalogItem = async () => {
    if (savingCatalogItem) return;
    if (!newCatName.trim()) { toast.error("Enter an item name"); return; }
    if (!quo.profile_id) { toast.error("Pick a business first"); return; }
    setSavingCatalogItem(true);
    try {
      const { data } = await api.post(`/items?profile_id=${quo.profile_id}`, { name: newCatName, sales_price: Number(newCatPrice || 0) });
      setCatalogItems((v) => [...v, data].sort((a, b) => a.name.localeCompare(b.name)));
      addFromCatalog(data);
      setNewCatName(""); setNewCatPrice("");
    } catch {
      toast.error("Failed to save catalog item");
    } finally {
      setSavingCatalogItem(false);
    }
  };

  const [saving, setSaving] = useState(false);
  const save = async (andPreview = false) => {
    if (saving) return;
    if (!quo.profile_id || !quo.client_id) { toast.error("Pick a business and a client"); return; }
    setSaving(true);
    try {
      const payload = {
        ...quo,
        discount: Number(quo.discount || 0),
        gst_rate: Number(quo.gst_rate || 0),
        items: quo.items.map((i) => ({ ...i, quantity: Number(i.quantity || 0), rate: Number(i.rate || 0), amount: Number(i.quantity || 0) * Number(i.rate || 0) })),
      };
      let saved;
      if (isNew) saved = await api.post("/quotations", payload);
      else saved = await api.put(`/quotations/${id}`, payload);
      toast.success(isNew ? "Quotation created" : "Quotation updated");
      if (andPreview) setShowPreview(true);
      else if (isNew) navigate(`/quotations/${saved.data.id}`, { replace: true });
    } catch {
      toast.error("Failed to save quotation");
    } finally {
      setSaving(false);
    }
  };

  const [converting, setConverting] = useState(false);
  const convertToInvoice = async () => {
    if (converting) return;
    if (!window.confirm("Convert this quotation into an invoice? This can't be undone.")) return;
    setConverting(true);
    try {
      const { data } = await api.post(`/quotations/${id}/convert`);
      toast.success("Converted to invoice");
      navigate(`/invoices/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to convert");
      setConverting(false);
    }
  };

  const profile = profiles.find((p) => p.id === quo.profile_id);
  const client = clients.find((c) => c.id === quo.client_id);

  if (showPreview) {
    const downloadPdf = () => {
      const el = document.getElementById("invoice-document-root");
      if (!el) return;
      const filename = `${quo.quotation_number || "quotation"}.pdf`;
      html2pdf().set({
        margin: 0,
        filename,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      }).from(el).save();
    };
    return (
      <div className="min-h-screen bg-neutral-900 print:bg-white">
        <div className="no-print sticky top-0 z-20 bg-[#0A0A0A] border-b border-white/10 px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <button data-testid="close-preview-btn" onClick={() => setShowPreview(false)} className="flex items-center gap-2 text-neutral-300 hover:text-white">
            <X className="w-4 h-4" strokeWidth={1.5} /> <span className="font-mont text-xs uppercase tracking-[0.2em]">Close Preview</span>
          </button>
          <div className="flex gap-3">
            <Button data-testid="download-pdf-btn" onClick={downloadPdf} className="rounded-none bg-transparent border border-white/20 text-white hover:bg-white/5">
              <Download className="w-4 h-4 mr-2" strokeWidth={1.5} /> Download PDF
            </Button>
            <Button data-testid="print-pdf-btn" onClick={() => window.print()} className="rounded-none bg-white text-black hover:bg-neutral-200">
              <Printer className="w-4 h-4 mr-2" strokeWidth={1.5} /> Print
            </Button>
          </div>
        </div>
        <div className="py-8 print:py-0">
          <InvoiceDocument invoice={quo} profile={profile} client={client} totals={totals} docType="quotation" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10">
      <button onClick={() => navigate("/quotations")} className="flex items-center gap-2 text-neutral-500 hover:text-white mb-6 font-mont text-xs uppercase tracking-[0.25em]">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back
      </button>

      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 lg:mb-10">
        <div>
          <p className="font-mont text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">— {isNew ? "Compose" : "Revise"}</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">{isNew ? "New Quotation" : quo.quotation_number}</h1>
          {isConverted && (
            <p className="font-mont text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mt-2">
              — Converted to invoice
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-3">
          {!isNew && !isConverted && (
            <Button data-testid="convert-invoice-btn" disabled={converting} onClick={convertToInvoice} className="rounded-none bg-[#D4AF37] text-black hover:bg-[#c4a032] px-6 py-5 disabled:opacity-50">
              <ArrowRightLeft className="w-4 h-4 mr-2" strokeWidth={1.5} /> {converting ? "Converting…" : "Convert to Invoice"}
            </Button>
          )}
          {!isConverted && (
            <>
              <Button data-testid="save-quotation-btn" disabled={saving} onClick={() => save(false)} className="rounded-none bg-transparent border border-white/20 text-white hover:bg-white/5 px-6 py-5 disabled:opacity-50">
                <Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> {saving ? "Saving…" : "Save"}
              </Button>
              <Button data-testid="save-preview-btn" disabled={saving} onClick={() => save(true)} className="rounded-none bg-white text-black hover:bg-neutral-200 px-6 py-5 disabled:opacity-50">
                <Printer className="w-4 h-4 mr-2" strokeWidth={1.5} /> {saving ? "Saving…" : "Save & Preview"}
              </Button>
            </>
          )}
          {isConverted && (
            <Button data-testid="preview-quotation-btn" onClick={() => setShowPreview(true)} className="rounded-none bg-white text-black hover:bg-neutral-200 px-6 py-5">
              <Printer className="w-4 h-4 mr-2" strokeWidth={1.5} /> Preview
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
        {/* LEFT: Form */}
        <div className="lg:col-span-8 space-y-6 lg:space-y-10">
          {/* Profile & Client */}
          <section className="bg-[#121212] border border-white/10 p-5 sm:p-8">
            <div className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-6">— Identity</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <Label className={labelCls}>From (Business)</Label>
                <Select disabled={isConverted} value={quo.profile_id} onValueChange={(v) => setQuo((s) => ({ ...s, profile_id: v }))}>
                  <SelectTrigger data-testid="select-profile" className="bg-transparent border-0 border-b border-white/20 rounded-none focus:ring-0 text-white font-mont mt-1 px-0">
                    <SelectValue placeholder="Select business" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white rounded-none">
                    {profiles.map((p) => (
                      <SelectItem key={p.id} value={p.id} className="font-mont text-sm">{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelCls}>For (Client)</Label>
                <Select disabled={isConverted} value={quo.client_id} onValueChange={(v) => setQuo((s) => ({ ...s, client_id: v }))}>
                  <SelectTrigger data-testid="select-client" className="bg-transparent border-0 border-b border-white/20 rounded-none focus:ring-0 text-white font-mont mt-1 px-0">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white rounded-none">
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id} className="font-mont text-sm">{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelCls}>Quotation No.</Label>
                <Input disabled={isConverted} data-testid="input-quotation-number" className={fieldCls} value={quo.quotation_number} onChange={(e) => setQuo((v) => ({ ...v, quotation_number: e.target.value }))} />
              </div>
              <div>
                <Label className={labelCls}>Issue Date</Label>
                <Input disabled={isConverted} data-testid="input-issue-date" type="date" className={fieldCls + " [color-scheme:dark]"} value={quo.issue_date} onChange={(e) => setQuo((v) => ({ ...v, issue_date: e.target.value }))} />
              </div>
              <div>
                <Label className={labelCls}>Valid Until</Label>
                <Input disabled={isConverted} type="date" className={fieldCls + " [color-scheme:dark]"} value={quo.valid_until} onChange={(e) => setQuo((v) => ({ ...v, valid_until: e.target.value }))} />
              </div>
            </div>
          </section>

          {/* Items */}
          <section className="bg-[#121212] border border-white/10 p-5 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500">— Line Items</div>
              {!isConverted && (
                <div className="flex gap-2">
                  <Dialog open={catalogOpen} onOpenChange={setCatalogOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="browse-catalog-btn" variant="ghost" className="text-neutral-300 hover:text-white hover:bg-white/5 rounded-none">
                        <ListPlus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Browse Catalog
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#121212] border-white/10 rounded-none text-white max-w-lg max-h-[85vh] overflow-y-auto">
                      <DialogHeader><DialogTitle className="font-serif text-2xl">Item Catalog</DialogTitle></DialogHeader>
                      <div className="space-y-4 mt-2">
                        <div className="relative">
                          <Search className="w-4 h-4 text-neutral-500 absolute left-0 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
                          <Input
                            data-testid="catalog-search-input"
                            value={catalogSearch}
                            onChange={(e) => setCatalogSearch(e.target.value)}
                            placeholder="Search saved items…"
                            className={fieldCls + " pl-6"}
                          />
                        </div>
                        <div className="max-h-64 overflow-y-auto divide-y divide-white/10 border border-white/10">
                          {catalogItems.filter((c) => c.name.toLowerCase().includes(catalogSearch.toLowerCase())).length === 0 ? (
                            <div className="py-8 text-center font-mont text-xs uppercase tracking-[0.25em] text-neutral-600">
                              {catalogItems.length === 0 ? "No saved items yet — add one below" : "No matches"}
                            </div>
                          ) : (
                            catalogItems
                              .filter((c) => c.name.toLowerCase().includes(catalogSearch.toLowerCase()))
                              .map((c) => (
                                <button
                                  key={c.id}
                                  data-testid={`catalog-item-${c.name}`}
                                  onClick={() => { addFromCatalog(c); setCatalogOpen(false); }}
                                  className="w-full flex items-center justify-between px-3 py-3 hover:bg-white/5 text-left"
                                >
                                  <span className="text-sm text-white truncate">{c.name}</span>
                                  <span className="font-mont text-sm text-[#D4AF37] flex-shrink-0 ml-3">{fmtINR(c.sales_price)}</span>
                                </button>
                              ))
                          )}
                        </div>
                        <div className="pt-3 border-t border-white/10">
                          <div className="font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">— New catalog item</div>
                          <div className="flex gap-2">
                            <Input data-testid="new-catalog-name" value={newCatName} onChange={(e) => setNewCatName(e.target.value)} placeholder="Item name" className={fieldCls + " flex-1"} />
                            <Input data-testid="new-catalog-price" type="number" step="any" value={newCatPrice} onChange={(e) => setNewCatPrice(e.target.value)} placeholder="Price" className={fieldCls + " w-28"} />
                            <Button data-testid="save-catalog-item-btn" disabled={savingCatalogItem} onClick={quickCreateCatalogItem} className="rounded-none bg-white text-black hover:bg-neutral-200 flex-shrink-0 disabled:opacity-50">{savingCatalogItem ? "Saving…" : "Save & Add"}</Button>
                          </div>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Button data-testid="add-item-btn" onClick={addItem} variant="ghost" className="text-neutral-300 hover:text-white hover:bg-white/5 rounded-none">
                    <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Add Item
                  </Button>
                </div>
              )}
            </div>

            <div className="hidden sm:grid grid-cols-12 gap-3 pb-3 border-b border-white/10 font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500">
              <div className="col-span-1"></div>
              <div className="col-span-5">Description</div>
              <div className="col-span-2 text-right">Qty</div>
              <div className="col-span-2 text-right">Rate</div>
              <div className="col-span-2 text-right">Amount</div>
            </div>

            {quo.items.map((it, idx) => (
              <div key={it.id} data-testid={`item-row-${idx}`} className="border-b border-white/10 py-4">
                {/* Mobile stacked layout */}
                <div className="sm:hidden space-y-2">
                  <div className="flex items-center gap-2">
                    <Input data-testid={`item-desc-${idx}`} value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} placeholder="Item description" className={fieldCls + " flex-1"} disabled={isConverted} />
                    {!isConverted && (
                      <button onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`} className="text-neutral-500 hover:text-red-400 flex-shrink-0">
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="font-mont text-[9px] uppercase text-neutral-600">Qty</Label>
                      <Input type="number" step="any" value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} className={fieldCls} disabled={isConverted} />
                    </div>
                    <div>
                      <Label className="font-mont text-[9px] uppercase text-neutral-600">Rate</Label>
                      <Input type="number" step="any" value={it.rate} onChange={(e) => setItem(idx, "rate", e.target.value)} className={fieldCls} disabled={isConverted} />
                    </div>
                    <div>
                      <Label className="font-mont text-[9px] uppercase text-neutral-600">Amount</Label>
                      <div className="font-mont text-sm text-white py-2">{fmtINR(Number(it.quantity || 0) * Number(it.rate || 0))}</div>
                    </div>
                  </div>
                </div>

                {/* Desktop grid layout */}
                <div className="hidden sm:grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-1 flex items-center gap-2">
                    {!isConverted && (
                      <button onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}-desktop`} className="text-neutral-500 hover:text-red-400">
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                  <Input data-testid={`item-desc-${idx}-desktop`} value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} placeholder="Item description" className={fieldCls + " col-span-5"} disabled={isConverted} />
                  <Input type="number" step="any" value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} className={fieldCls + " col-span-2 text-right"} disabled={isConverted} />
                  <Input type="number" step="any" value={it.rate} onChange={(e) => setItem(idx, "rate", e.target.value)} className={fieldCls + " col-span-2 text-right"} disabled={isConverted} />
                  <div className="col-span-2 text-right font-mont text-sm text-white">
                    {fmtINR(Number(it.quantity || 0) * Number(it.rate || 0))}
                  </div>
                </div>
              </div>
            ))}
          </section>

          {/* Notes / Terms / GST */}
          <section className="bg-[#121212] border border-white/10 p-5 sm:p-8 space-y-6">
            <div className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500">— Tax &amp; Terms</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <Label className={labelCls}>GST Rate (%)</Label>
                <Input type="number" step="any" disabled={isConverted} value={quo.gst_rate} onChange={(e) => setQuo((v) => ({ ...v, gst_rate: e.target.value }))} className={fieldCls} />
              </div>
              <div>
                <Label className={labelCls}>GST Type</Label>
                <Select disabled={isConverted} value={quo.gst_type} onValueChange={(v) => setQuo((s) => ({ ...s, gst_type: v }))}>
                  <SelectTrigger className="bg-transparent border-0 border-b border-white/20 rounded-none focus:ring-0 text-white font-mont mt-1 px-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white rounded-none">
                    <SelectItem value="CGST_SGST" className="font-mont text-sm">CGST + SGST</SelectItem>
                    <SelectItem value="IGST" className="font-mont text-sm">IGST</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelCls}>Discount</Label>
                <Input type="number" step="any" disabled={isConverted} value={quo.discount} onChange={(e) => setQuo((v) => ({ ...v, discount: e.target.value }))} className={fieldCls} />
              </div>
              <div>
                <Label className={labelCls}>Discount Type</Label>
                <Select disabled={isConverted} value={quo.discount_type} onValueChange={(v) => setQuo((s) => ({ ...s, discount_type: v }))}>
                  <SelectTrigger className="bg-transparent border-0 border-b border-white/20 rounded-none focus:ring-0 text-white font-mont mt-1 px-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white rounded-none">
                    <SelectItem value="flat" className="font-mont text-sm">Flat</SelectItem>
                    <SelectItem value="percent" className="font-mont text-sm">Percent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className={labelCls}>Notes</Label>
              <Textarea disabled={isConverted} value={quo.notes} onChange={(e) => setQuo((v) => ({ ...v, notes: e.target.value }))} className="bg-transparent border border-white/20 rounded-none focus-visible:ring-0 focus-visible:border-[#D4AF37] text-white font-mont" />
            </div>
            <div>
              <Label className={labelCls}>Terms &amp; Conditions</Label>
              <Textarea disabled={isConverted} value={quo.terms} onChange={(e) => setQuo((v) => ({ ...v, terms: e.target.value }))} className="bg-transparent border border-white/20 rounded-none focus-visible:ring-0 focus-visible:border-[#D4AF37] text-white font-mont" />
            </div>
          </section>
        </div>

        {/* RIGHT: Totals summary */}
        <div className="lg:col-span-4">
          <section className="bg-[#121212] border border-white/10 p-5 sm:p-8 sticky top-6">
            <div className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-6">— Summary</div>
            <div className="space-y-3 font-mont text-sm">
              <div className="flex justify-between text-neutral-300"><span>Subtotal</span><span>{fmtINR(totals.subtotal)}</span></div>
              {totals.discount_amount > 0 && (
                <div className="flex justify-between text-neutral-300"><span>Discount</span><span>− {fmtINR(totals.discount_amount)}</span></div>
              )}
              <div className="flex justify-between text-neutral-300"><span>Taxable</span><span>{fmtINR(totals.taxable)}</span></div>
              <div className="flex justify-between text-neutral-300"><span>GST ({quo.gst_rate}%)</span><span>{fmtINR(totals.gst_amount)}</span></div>
              <div className="flex justify-between text-white font-semibold text-base pt-3 border-t border-white/10"><span>Grand Total</span><span>{fmtINR(totals.grand_total)}</span></div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
